import React, { useState } from 'react';
import {
    Box,
    Card,
    Chip,
    IconButton,
    Typography,
    Stack,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    TextField,
    MenuItem
} from '@mui/material';
import { Visibility, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useComplaints } from 'hooks/useComplaints';
import { format } from 'date-fns';

const ComplaintList = () => {
    const navigate = useNavigate();
    const { data: complaints, isLoading } = useComplaints();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const filteredComplaints = (complaints || []).filter((complaint) => {
        const matchesSearch =
            complaint?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            complaint?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            complaint?.ticketId?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || complaint.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pending" color="warning" size="small" />;
            case 'reviewed':
                return <Chip label="Reviewed" color="info" size="small" />;
            case 'resolved':
                return <Chip label="Resolved" color="success" size="small" />;
            default:
                return <Chip label={status || 'Unknown'} size="small" />;
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={800} color="primary.main">
                    Support Tickets & Complaints
                </Typography>
            </Stack>

            <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                        fullWidth
                        placeholder="Search tickets by ID, title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        select
                        label="Status Filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="all">All Targets</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="reviewed">Reviewed</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                    </TextField>
                </Stack>
            </Card>

            <Card sx={{ p: 2, borderRadius: 3 }}>
                <TableContainer component={Paper} elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <TableCell>
                                    <strong>Ticket ID</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Date</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Reporter</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Subject</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Status</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredComplaints.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        No complaints found matching your criteria.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredComplaints.map((complaint) => (
                                    <TableRow key={complaint._id} hover>
                                        <TableCell>{complaint.ticketId || complaint._id.substring(0, 8)}</TableCell>
                                        <TableCell>{format(new Date(complaint.createdAt), 'MMM dd, yyyy')}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {complaint.user?.firstName} {complaint.user?.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {complaint.userType || 'Customer'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap>
                                                {complaint.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{getStatusChip(complaint.status)}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Visibility />}
                                                onClick={() => navigate(`/admin/complaints/${complaint._id}`)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
};

export default ComplaintList;
